import React from 'react';
import PropTypes from 'prop-types';
import {getWindow} from 'ssr-window';

import {Button} from '@spothero/ui';

/**
 * ### ExtendedButton
 * Extends the functionality of the fe-ui button. Takes in either an external
 * URL or an ID for the page that the button is used on. If an external URL
 * is provided, the Button is rendered as a styled anchor tag. If an ID is
 * provided, the onClick function fires `scrollIntoView()` for the ID provided.
 * External links will always take precedent over a section ID Uses the provided
 * style variant to apply styling to the button. For a full list of style variants
 * please see: https://spothero.github.io/fe-ui/?path=/story/v2-button--variant.
 *
 * Can Optionally take in a `Key` value for mapping over multiple Buttons, and any
 * other style props needed to style the button.
 *
 * For a full list of style props supported please see:
 *
 * - https://spothero.github.io/fe-ui/?path=/story/v2-button
 * - https://chakra-ui.com/docs/form/button#props
 *
 *
 * @function ExtendedButton
 * @param {object} buttonOptions
 * @param {string} buttonOptions.buttonText - The text on the button.
 * @param {string} buttonOptions.inPageAnchor - An internal ID on the same page that the button is used on.
 * @param {(string|number)} [buttonOptions.key=0] - (Optional) The key to be used on the Button.
 * @param {string} buttonOptions.link - An external URL to navigate to. If an external URL is
 * provided the Button will be rendered as a styled anchor tag.
 * @param {string} buttonOptions.linkTarget - The target to use with the provided link
 * @param {string} [buttonOptions.variant=primary] - The Style variant of the button.
 * @param {any} [buttonOptions.styleProps] - (Optional) Any other style props to be applied to the button.
 * @example
 * const buttonOneOptions = {
 *      buttonText: 'Click Here',
 *      link: 'https://google.com/',
 *      variant: 'primary',
 * };
 * <>
 *      {ExtendedButton(buttonOneOptions)}
 * </>
 * @returns {React.ReactNode}
 * The fe-ui Button component with the style variant and correct behavior applied.
 */

export const ExtendedButton = ({
    buttonText,
    inPageAnchor,
    key = 0,
    link,
    linkTarget,
    variant = 'primary',
    ...styleProps
} = {}) => {
    const window = getWindow();

    const buttonLinkProps = link
        ? {
              asAnchor: true,
              href: link,
              isExternal: linkTarget === '_blank',
          }
        : {
              /* --------------------------------------------------- *\
                    Opted for this method of scrolling to the desired
                    section since it's both easier to read through
                    and more straightforward than using the
                    `AnimationUtils.scrollTo()` approach.
                    TODO: Should the Front End chapter take a look at
                    the fe-utils repo at somepoint and reassess what
                    is still actually valuable to us and if what's
                    there is worth refactoring?
                \* --------------------------------------------------- */
              onClick: () => {
                  window.document.getElementById(inPageAnchor).scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                  });
              },
          };

    return (
        <Button
            key={key}
            variant={variant}
            {...buttonLinkProps}
            {...styleProps}
        >
            {buttonText}
        </Button>
    );
};

ExtendedButton.propTypes = {
    /** Text to be displayed on the Button. */
    buttonText: PropTypes.string.isRequired,
    /** An ID to scroll to on the page. */
    inPageAnchor: PropTypes.string,
    /** (Optional) The Key of the button. Use if mapping multiple buttons. */
    key: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    /** URL to Link off to. */
    link: PropTypes.string,
    /** target to set on the link */
    linkTarget: PropTypes.string,
    /** The Style variant to be applied to the button. */
    variant: PropTypes.string,
    /** (Optional) Any other style props to be applied to the button. */
    styleProps: PropTypes.any,
};

export default ExtendedButton;
